import React, { useState, useEffect, useCallback } from 'react';
import { tdsReturnAPI, formatCurrency } from '../services/api';
import { Download } from 'lucide-react';

export default function TDSReturnsPage() {
  const [tab, setTab] = useState('summary');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;

    const fn = tab === 'summary' ? tdsReturnAPI.summary :
               tab === 'payable' ? tdsReturnAPI.payable : tdsReturnAPI.receivable;
    fn(params).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [tab, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportJSON = (d, filename) => {
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="tds-returns-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">TDS Returns</h1>
          <p className="text-sm text-slate-500 mt-1">26Q-ready TDS summary and reports</p>
        </div>
        {data && (
          <button onClick={() => exportJSON(data, `TDS_${tab}_${dateFrom || 'all'}.json`)}
            className="btn-accent flex items-center gap-2 text-sm" data-testid="export-tds-json">
            <Download className="w-4 h-4" /> Export JSON
          </button>
        )}
      </div>

      {/* Tabs + Date Filter */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-md p-3">
        <div className="flex gap-1 bg-slate-100 rounded-md p-0.5">
          {[
            { k: 'summary', l: 'Full Summary' },
            { k: 'payable', l: 'TDS Payable' },
            { k: 'receivable', l: 'TDS Receivable' },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${tab === t.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tds-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="form-input w-40" data-testid="tds-date-from" />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="form-input w-40" data-testid="tds-date-to" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'summary' && data ? (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { l: 'Total Entries', v: data.summary?.total_entries },
              { l: 'Unique Parties', v: data.summary?.unique_parties },
              { l: 'Total Taxable', v: `Rs.${formatCurrency(data.summary?.total_taxable)}` },
              { l: 'Total TDS', v: `Rs.${formatCurrency(data.summary?.total_tds)}`, c: 'text-indigo-700' },
              { l: 'TDS Payable', v: `Rs.${formatCurrency(data.summary?.tds_payable)}`, c: 'text-rose-700' },
              { l: 'TDS Receivable', v: `Rs.${formatCurrency(data.summary?.tds_receivable)}`, c: 'text-emerald-700' },
            ].map((m, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-md p-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase">{m.l}</p>
                <p className={`text-base font-bold font-mono mt-1 ${m.c || 'text-slate-900'}`}>{m.v}</p>
              </div>
            ))}
          </div>

          {/* Section Breakdown */}
          {data.sections?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="tds-sections">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">Section-wise Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead><tr><th>Section</th><th className="text-right">Entries</th><th className="text-right">Taxable Value</th><th className="text-right">TDS Amount</th></tr></thead>
                  <tbody>
                    {data.sections.map((s, i) => (
                      <tr key={i}>
                        <td className="font-mono text-sm font-medium">{s.section}</td>
                        <td className="text-right font-mono text-sm">{s.count}</td>
                        <td className="text-right font-mono text-sm">Rs.{formatCurrency(s.total_taxable)}</td>
                        <td className="text-right font-mono text-sm font-medium text-indigo-700">Rs.{formatCurrency(s.total_tds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Party-wise (26Q) */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="tds-parties">
            <h3 className="font-heading font-semibold text-slate-800 mb-3">Party-wise Summary (26Q Ready)</h3>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>Party Name</th><th>PAN</th><th>Sections</th><th className="text-right">Invoices</th><th className="text-right">Taxable</th><th className="text-right">TDS Amount</th></tr></thead>
                <tbody>
                  {data.parties?.map((p, i) => (
                    <tr key={i}>
                      <td className="text-sm">{p.party_name}</td>
                      <td className="font-mono text-sm">{p.pan || '-'}</td>
                      <td className="text-sm">{(p.sections || []).join(', ')}</td>
                      <td className="text-right font-mono text-sm">{p.invoice_count}</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(p.total_taxable)}</td>
                      <td className="text-right font-mono text-sm font-medium text-indigo-700">Rs.{formatCurrency(p.total_tds)}</td>
                    </tr>
                  ))}
                  {!data.parties?.length && <tr><td colSpan={6} className="text-center text-slate-400 py-6">No TDS entries found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* All Entries */}
          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="tds-entries">
            <h3 className="font-heading font-semibold text-slate-800 mb-3">All TDS Entries</h3>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>Invoice</th><th>Date</th><th>Type</th><th>Party</th><th>PAN</th><th>Section</th><th className="text-right">Rate</th><th className="text-right">Taxable</th><th className="text-right">TDS</th></tr></thead>
                <tbody>
                  {data.entries?.map((e, i) => (
                    <tr key={i}>
                      <td className="font-mono text-sm">{e.invoice_number}</td>
                      <td className="text-sm text-slate-500">{e.invoice_date}</td>
                      <td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${e.voucher_type === 'sales' ? 'bg-blue-50 text-blue-700' : 'bg-rose-50 text-rose-700'}`}>{e.voucher_type}</span></td>
                      <td className="text-sm">{e.party_name}</td>
                      <td className="font-mono text-xs">{e.pan || '-'}</td>
                      <td className="font-mono text-sm">{e.tds_section}</td>
                      <td className="text-right font-mono text-sm">{e.tds_percentage}%</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(e.taxable_value)}</td>
                      <td className="text-right font-mono text-sm font-medium text-indigo-700">Rs.{formatCurrency(e.tds_amount)}</td>
                    </tr>
                  ))}
                  {!data.entries?.length && <tr><td colSpan={9} className="text-center text-slate-400 py-6">No TDS entries</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (tab === 'payable' || tab === 'receivable') && data ? (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-2">
              {tab === 'payable' ? 'TDS Payable (To Deposit)' : 'TDS Receivable (To Claim)'}
            </h3>
            <p className="text-2xl font-bold font-mono text-indigo-700 mb-4">
              Rs.{formatCurrency(tab === 'payable' ? data.total_payable : data.total_receivable)}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead><tr><th>Party</th>{tab === 'payable' && <th>PAN</th>}<th>Section</th><th className="text-right">Count</th><th className="text-right">Taxable</th><th className="text-right">TDS</th></tr></thead>
                <tbody>
                  {data.entries?.map((e, i) => (
                    <tr key={i}>
                      <td className="text-sm">{e.party_name}</td>
                      {tab === 'payable' && <td className="font-mono text-xs">{e.pan || '-'}</td>}
                      <td className="font-mono text-sm">{e.tds_section}</td>
                      <td className="text-right font-mono text-sm">{e.count}</td>
                      <td className="text-right font-mono text-sm">Rs.{formatCurrency(e.total_taxable)}</td>
                      <td className="text-right font-mono text-sm font-medium text-indigo-700">Rs.{formatCurrency(e.total_tds)}</td>
                    </tr>
                  ))}
                  {!data.entries?.length && <tr><td colSpan={tab === 'payable' ? 6 : 5} className="text-center text-slate-400 py-6">No entries</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
